import { BooleanFeature as BooleanFeatureComponent } from 'mk2/apps/strollers/components/features/BooleanFeature';
import { CountriesFeature as CountriesFeatureComponent } from 'mk2/apps/strollers/components/features/CountriesFeature';
import { MinMaxDimensionsFeature as MinMaxDimensionsFeatureComponent } from 'mk2/apps/strollers/components/features/MinMaxDimensionsFeature';
import { NumericOptionalRangeFeature as NumericOptionalRangeFeatureComponent } from 'mk2/apps/strollers/components/features/NumericOptionalRangeFeature';
import { PlainFeature as PlainFeatureComponent } from 'mk2/apps/strollers/components/features/PlainFeature';
import { TwoDimsFeature as TwoDimsFeatureComponent } from 'mk2/apps/strollers/components/features/TwoDimsFeature';
import { FEATURE_LABEL_MESSAGES } from 'mk2/apps/strollers/features/constants';
import { Features } from 'mk2/apps/strollers/schemas';
import { niceNumber } from 'mk2/apps/strollers/utils';

export interface FeatureOptions {
    nice?: boolean;
    infinityName?: string;
    sorted?: boolean;
}

export class Feature {
    public Component = PlainFeatureComponent;
    public featureName: string;
    public options: FeatureOptions;

    constructor(featureName: string, options: FeatureOptions = {}) {
        this.featureName = featureName;
        this.options = { ...this.getDefaultOptions(), ...options };
    }

    public getDefaultOptions(): FeatureOptions {
        return { nice: true, sorted: true };
    }

    public getLabelMessage(): string {
        return FEATURE_LABEL_MESSAGES[this.featureName];
    }

    public hasWorthyValue(features: Features): boolean {
        if (!features) {
            return false;
        }
        const value = features[this.featureName];
        return (value !== undefined) && (value !== null);
    }

    public rawValue(features: Features): any {
        if (!features) {
            return null;
        }
        return features[this.featureName];
    }

    public niceValue(features: Features): string {
        if (!features) {
            return '';
        }
        const value = features[this.featureName];
        return value ? value.toString() : '';
    }
}

export class BooleanFeature extends Feature {
    public Component = BooleanFeatureComponent;

    public hasWorthyValue(features: Features): boolean {
        if (!features) {
            return false;
        }
        const value = features[this.featureName];
        return !!value;
    }

    public niceValue(features: Features): string {
        if (!features) {
            return '';
        }
        const value = features[this.featureName];
        switch (value) {
            case true:
                return 'áno';
            case false:
                return 'nie';
            default:
                return '';
        }
    }
}

export class TextFeature extends Feature {

    public niceValue(features: Features): string {
        if (!features) {
            return '';
        }
        const value = features[this.featureName];
        if (!value) {
            return '';
        }
        return value;
    }
}

export class NumericFeature extends Feature {

    public niceValue(features: Features): string {
        if (!features) {
            return '';
        }
        const value = features[this.featureName];
        if (!value) {
            return '';
        }
        return this.options.nice ? niceNumber(value) : value;
    }
}

export class NumericListFeature extends Feature {

    public decodeItem(value) {
        return this.options.nice ? niceNumber(value) : value;
    }

    public niceValue(features: Features): string {
        const arrValue: number[] = this.rawValue(features) as number[];
        if (!arrValue) {
            return '';
        }

        const translated = arrValue.map((value) => this.decodeItem(value));
        if (this.options.sorted) {
            translated.sort();
        }
        return translated.join(', ');
    }
}

export class InfiniteNumericListFeature extends NumericListFeature {
    private INFINITY = 999999999;

    public decodeItem(value) {
        if (value === this.INFINITY) {
            return this.options.infinityName;
        }
        return super.decodeItem(value);
    }
}

export class NumericOptionalRangeFeature extends Feature {
    public Component = NumericOptionalRangeFeatureComponent;

    public hasWorthyValue(features: Features): boolean {
        if (!features) {
            return false;
        }
        const valueMax = features[this.featureName + '_max'];
        const valueMin = features[this.featureName + '_min'];
        return !!(valueMax || valueMin);
    }

    public niceValue(features: Features): string {
        if (!features) {
            return '';
        }
        const valueMax = features[this.featureName + '_max'];
        const valueMin = features[this.featureName + '_min'];
        if ((valueMax !== undefined) && (valueMin !== undefined) && (valueMax !== valueMin)) {
            return `${niceNumber(valueMin)}\u00A0\u2013\u00A0${niceNumber(valueMax)}`;     // beware: nbsp + ndash used
        }
        const value = valueMax || valueMin;
        return value ? niceNumber(value) : '';
    }
}

interface WidthDepth {
    depth: number;
    width: number;
}

export class WidthDepthFeature extends Feature {
    public Component = TwoDimsFeatureComponent;

    public getDims(features: Features): WidthDepth {
        return {
            depth: features[this.featureName + '_depth'],
            width: features[this.featureName + '_width'],
        };
    }

    public hasWorthyValue(features: Features): boolean {
        const dims = this.getDims(features);
        return !!(dims.depth || dims.width);
    }
}

interface MinMaxDimensions {
    heightMax: number;
    heightMin: number;
    lengthMax: number;
    lengthMin: number;
    widthMax: number;
    widthMin: number;
}

export class MinMaxDimensionsFeature extends Feature {
    public Component = MinMaxDimensionsFeatureComponent;

    public getDims(features: Features): MinMaxDimensions {
        return {
            heightMax: features[this.featureName + '_height_max'],
            heightMin: features[this.featureName + '_height_min'],
            lengthMax: features[this.featureName + '_length_max'],
            lengthMin: features[this.featureName + '_length_min'],
            widthMax: features[this.featureName + '_width_max'],
            widthMin: features[this.featureName + '_width_min'],
        };
    }

    public hasWorthyValue(features: Features): boolean {
        const dims = this.getDims(features);
        return !!(
            dims.heightMax || dims.heightMin
            || dims.lengthMax || dims.lengthMin
            || dims.widthMax || dims.widthMin);
    }
}

export class CountriesFeature extends Feature {
    public Component = CountriesFeatureComponent;
}
